/**
 * BulkLineActions — barre d'actions flottante qui apparaît quand ≥1 ligne
 * est sélectionnée via les checkboxes du tableau.
 *
 * Actions : accepter, rejeter, marquer en waiting_client (avec question).
 */

import { useState } from 'react';
import { Check, X, Clock, Wrench } from 'lucide-react';
import { api } from '../lib/api-client';
import { IconButton } from './IconButton';
import type { TenderLine } from '../lib/types';

type Props = {
  tenderId: string;
  selectedLineIds: string[];
  /** Lignes sélectionnées (pour la correction SKU en lot au checkpoint). */
  selectedLines?: TenderLine[];
  /** true en checkpoint runner : active la correction SKU/règle en lot. */
  checkpoint?: boolean;
  onDone: () => void;
  onClear: () => void;
};

export function BulkLineActions({ tenderId, selectedLineIds, selectedLines = [], checkpoint, onDone, onClear }: Props) {
  const [submitting, setSubmitting] = useState(false);
  const [mode, setMode] = useState<'idle' | 'wait' | 'sku'>('idle');
  const [question, setQuestion] = useState('');
  const [comment, setComment] = useState('');
  const [bulkSku, setBulkSku] = useState('');
  const [bulkPromote, setBulkPromote] = useState(false);

  // Lignes sélectionnées rattachées à une résolution runner (corrigibles via /answers).
  const runnerLines = selectedLines.filter((l) => {
    const s = (l.specsExtraites ?? {}) as Record<string, unknown>;
    return Boolean(s.table ?? s.source_table);
  });

  async function runSkuCorrection() {
    if (!bulkSku.trim() || runnerLines.length === 0) return;
    setSubmitting(true);
    try {
      for (const l of runnerLines) {
        const s = (l.specsExtraites ?? {}) as Record<string, unknown>;
        await api.post(`/api/tenders/${tenderId}/answers`, {
          source: 'references',
          code: 'reference_needs_attention',
          table: (s.table ?? s.source_table ?? null) as string | null,
          itemId: (s.item_id ?? null) as string | null,
          lotId: l.lot ?? null,
          ref: bulkSku.trim(),
          answer: `Correction en lot (${runnerLines.length} lignes)`,
          promoteToRule: bulkPromote,
          ruleNote: bulkPromote ? `SKU ${bulkSku.trim()} appliqué en lot` : null,
        });
      }
      setBulkSku('');
      setBulkPromote(false);
      setMode('idle');
      onDone();
      onClear();
    } finally {
      setSubmitting(false);
    }
  }

  async function runAction(action: 'accept' | 'reject' | 'wait_client') {
    setSubmitting(true);
    try {
      await api.patch(`/api/tenders/${tenderId}/lines/bulk`, {
        lineIds: selectedLineIds,
        action,
        commentaireRevue: comment || undefined,
        questionClient: action === 'wait_client' ? question : undefined,
      });
      setQuestion('');
      setComment('');
      setMode('idle');
      onDone();
      onClear();
    } finally {
      setSubmitting(false);
    }
  }

  if (selectedLineIds.length === 0) return null;

  return (
    <div
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40
                 bg-white text-gray-900 border border-gray-200 dark:bg-gray-900 dark:text-white dark:border-transparent rounded-lg shadow-2xl px-4 py-3
                 flex flex-col gap-2 min-w-[500px] max-w-[700px]"
      role="region"
      aria-label="Actions en masse"
    >
      {mode === 'idle' && (
        <div className="flex items-center gap-3">
          <div className="font-medium text-sm">
            {selectedLineIds.length} ligne{selectedLineIds.length > 1 ? 's' : ''} sélectionnée
            {selectedLineIds.length > 1 ? 's' : ''}
          </div>
          <div className="flex-1" />
          <button
            onClick={() => runAction('accept')}
            disabled={submitting}
            className="bg-green-600 hover:bg-green-700 px-3 py-1.5 rounded text-sm disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            <Check aria-hidden className="w-4 h-4" strokeWidth={1.75} />
            <span>Accepter tout</span>
          </button>
          <button
            onClick={() => runAction('reject')}
            disabled={submitting}
            className="bg-red-600 hover:bg-red-700 px-3 py-1.5 rounded text-sm disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            <X aria-hidden className="w-4 h-4" strokeWidth={1.75} />
            <span>Rejeter tout</span>
          </button>
          <button
            onClick={() => setMode('wait')}
            disabled={submitting}
            className="bg-blue-600 hover:bg-blue-700 px-3 py-1.5 rounded text-sm disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            <Clock aria-hidden className="w-4 h-4" strokeWidth={1.75} />
            <span>Demander au client</span>
          </button>
          {checkpoint && runnerLines.length > 0 && (
            <button
              onClick={() => setMode('sku')}
              disabled={submitting}
              title={`Appliquer un même SKU aux ${runnerLines.length} ligne(s) runner sélectionnée(s)`}
              className="bg-amber-600 hover:bg-amber-700 px-3 py-1.5 rounded text-sm disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              <Wrench aria-hidden className="w-4 h-4" strokeWidth={1.75} />
              <span>Corriger SKU ({runnerLines.length})</span>
            </button>
          )}
          <IconButton
            icon={X}
            label="Fermer"
            size="sm"
            tooltipSide="top"
            onClick={onClear}
            disabled={submitting}
          />
        </div>
      )}

      {mode === 'sku' && (
        <div className="flex flex-col gap-2">
          <div className="text-sm">
            Appliquer un même SKU aux {runnerLines.length} ligne(s) sélectionnée(s) (pris au « Recalculer ») :
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <input
              value={bulkSku}
              onChange={(e) => setBulkSku(e.target.value)}
              placeholder="SKU catalogue (ex: A-005-0200)"
              className="flex-1 min-w-[220px] rounded px-2 py-1 text-sm bg-gray-100 text-gray-900 border border-gray-300 dark:bg-gray-800 dark:text-white dark:border-gray-700 font-mono"
            />
            <label className="inline-flex items-center gap-1.5 text-xs text-amber-300 cursor-pointer">
              <input type="checkbox" checked={bulkPromote} onChange={(e) => setBulkPromote(e.target.checked)} />
              ⭐ règle générale
            </label>
            <button
              onClick={runSkuCorrection}
              disabled={submitting || !bulkSku.trim()}
              className="bg-amber-600 hover:bg-amber-700 px-3 py-1.5 rounded text-sm disabled:opacity-50"
            >
              {submitting ? 'Application…' : 'Appliquer'}
            </button>
            <button onClick={() => setMode('idle')} disabled={submitting} className="text-gray-500 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white px-3 py-1.5 rounded text-sm">
              Retour
            </button>
          </div>
        </div>
      )}

      {mode === 'wait' && (
        <div className="flex flex-col gap-2">
          <div className="text-sm">
            Question commune pour le client (appliquée aux {selectedLineIds.length} lignes) :
          </div>
          <textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Ex: Confirmer la norme ATEX requise pour cette zone ?"
            className="w-full rounded px-2 py-1 text-sm bg-gray-100 text-gray-900 border border-gray-300 dark:bg-gray-800 dark:text-white dark:border-gray-700"
            rows={3}
            maxLength={2000}
          />
          <div className="flex gap-2 items-center">
            <button
              onClick={() => runAction('wait_client')}
              disabled={submitting || !question.trim()}
              className="bg-blue-600 hover:bg-blue-700 px-3 py-1.5 rounded text-sm disabled:opacity-50"
            >
              Envoyer
            </button>
            <button
              onClick={() => setMode('idle')}
              disabled={submitting}
              className="text-gray-500 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white px-3 py-1.5 rounded text-sm"
            >
              Retour
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
